import { getFirestore, collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, addDoc, query, where, writeBatch, arrayUnion, arrayRemove } from "firebase/firestore";
import { Audit, Finding, Incident, CorrectiveAction } from "./index";

export interface AuditServiceOptions {
  activeTenancy: { id: string };
}

export class AuditService {
  private db = getFirestore();
  private tenancyId: string;

  constructor(options: AuditServiceOptions) {
    this.tenancyId = options.activeTenancy.id;
  }

  // --- Audit CRUD ---
  auditsCollection() {
    return collection(this.db, `customers/${this.tenancyId}/audits`);
  }

  async listAudits(): Promise<Audit[]> {
    const snap = await getDocs(this.auditsCollection());
    // Handle if doesnt exist, means we gotta create
    // if (snap.empty) {
    //   await this.createAudit({
    //     type: "Initial Audit",
    //     scope: "Initial Audit",
    //     owners: [],
    //     auditor: "",
    //     findings: [], // Will be populated separately
    //     incidents: [], // Will be populated separately
    //   });

    //   const snap2 = await getDocs(this.auditsCollection());
    //   return snap2.docs.map(doc => ({ id: doc.id, ...doc.data() } as Audit));

    // }
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Audit));
  }

  async getAudit(auditId: string): Promise<Audit | null> {
    const ref = doc(this.db, `customers/${this.tenancyId}/audits/${auditId}`);
    const snap = await getDoc(ref);
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as Audit) : null;
  }

  async createAudit(audit: Omit<Audit, "id">): Promise<string> {
    const ref = await addDoc(this.auditsCollection(), {
      ...audit,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return ref.id;
  }

  async updateAudit(auditId: string, data: Partial<Audit>) {
    const ref = doc(this.db, `customers/${this.tenancyId}/audits/${auditId}`);
    await updateDoc(ref, { ...data, updatedAt: new Date() });
  }

  async deleteAudit(auditId: string) {
    const ref = doc(this.db, `customers/${this.tenancyId}/audits/${auditId}`);
    await deleteDoc(ref);
  }

  // --- Findings (Non-Conformities) ---
  findingsCollection(auditId: string) {
    return collection(this.db, `customers/${this.tenancyId}/audits/${auditId}/findings`);
  }

  async listFindings(auditId: string): Promise<Finding[]> {
    const snap = await getDocs(this.findingsCollection(auditId));
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Finding));
  }

  async createFinding(auditId: string, finding: Omit<Finding, 'id'>) {
    const ref = collection(this.db, `customers/${this.tenancyId}/audits/${auditId}/findings`);
    const docRef = await addDoc(ref, {
      ...finding,
      origin: 'audit' as const,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  }

  async updateFinding(auditId: string, findingId: string, data: Partial<Finding>) {
    const ref = doc(this.db, `customers/${this.tenancyId}/audits/${auditId}/findings/${findingId}`);
    const updateData: Record<string, any> = { ...data, updatedAt: new Date() };

    // DED-973: Ensure linkedDocuments array is properly saved
    if ('linkedDocuments' in data) {
      updateData.linkedDocuments = Array.isArray(data.linkedDocuments) ? data.linkedDocuments : [];
    }

    // Remove undefined values
    Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);

    await updateDoc(ref, updateData);
  }

  async deleteFinding(auditId: string, findingId: string) {
    const ref = doc(this.db, `customers/${this.tenancyId}/audits/${auditId}/findings/${findingId}`);
    await deleteDoc(ref);
  }

  // --- Corrective Action Methods ---
  private correctiveActionsCollection() {
    return collection(this.db, `customers/${this.tenancyId}/correctiveActions`);
  }

  async listStandaloneCorrectiveActions(): Promise<CorrectiveAction[]> {
    const snap = await getDocs(this.correctiveActionsCollection());
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as CorrectiveAction));
  }

  async getCorrectiveAction(actionId: string): Promise<CorrectiveAction | null> {
    const ref = doc(this.db, `customers/${this.tenancyId}/correctiveActions/${actionId}`);
    const snap = await getDoc(ref);
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as CorrectiveAction) : null;
  }

  async createStandaloneCorrectiveAction(action: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>): Promise<CorrectiveAction> {
    const now = new Date();
    // Create a clean copy of the action data without undefined values
    const cleanAction = Object.entries(action).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      } else {
        acc[key] = null;
      }
      return acc;
    }, {} as Record<string, any>);

    const ref = await addDoc(this.correctiveActionsCollection(), {
      ...cleanAction,
      createdAt: now,
      updatedAt: now,
    });
    return {
      id: ref.id,
      ...cleanAction,
      createdAt: now,
      updatedAt: now
    } as CorrectiveAction;
  }

  async updateStandaloneCorrectiveAction(actionId: string, data: Partial<Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>>) {
    const ref = doc(this.db, `customers/${this.tenancyId}/correctiveActions/${actionId}`);
    // DED-973: Ensure linkedDocuments array is properly saved
    const updateData: Record<string, any> = { ...data, updatedAt: new Date() };
    
    // Ensure linkedDocuments is always an array (not undefined)
    if ('linkedDocuments' in data) {
      updateData.linkedDocuments = Array.isArray(data.linkedDocuments) ? data.linkedDocuments : [];
    }
    
    await updateDoc(ref, updateData);
  }

  async deleteStandaloneCorrectiveAction(actionId: string) {
    const ref = doc(this.db, `customers/${this.tenancyId}/correctiveActions/${actionId}`);
    await deleteDoc(ref);
  }

  async linkCorrectiveActionToFinding(actionId: string, findingId: string) {
    const actionRef = doc(this.db, `customers/${this.tenancyId}/correctiveActions/${actionId}`);
    await updateDoc(actionRef, {
      findingId,
      updatedAt: new Date()
    });
  }

  async unlinkCorrectiveAction(actionId: string) {
    const actionRef = doc(this.db, `customers/${this.tenancyId}/correctiveActions/${actionId}`);
    await updateDoc(actionRef, {
      findingId: null,
      finding: null,
      updatedAt: new Date()
    });
  }

  async unlinkCorrectiveActionFromAudit(actionId: string) {
    const actionRef = doc(this.db, `customers/${this.tenancyId}/correctiveActions/${actionId}`);
    await updateDoc(actionRef, {
      auditId: null,
      updatedAt: new Date()
    });
  }

  async getCorrectiveActionsForFinding(findingId: string): Promise<CorrectiveAction[]> {
    if (!findingId) return [];

    const q = query(
      collection(this.db, `customers/${this.tenancyId}/correctiveActions`),
      where('findingId', '==', findingId)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CorrectiveAction));
  }

  // --- Incidents ---
  incidentsCollection() {
    return collection(this.db, `customers/${this.tenancyId}/incidents`);
  }

  async listIncidents(): Promise<Incident[]> {
    const snap = await getDocs(this.incidentsCollection());
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Incident));
  }

  async createIncident(incident: Omit<Incident, "id"> & { auditId?: string }): Promise<Incident> {
    console.log('createIncident called with incident:', incident);
    console.log('Using collection path:', `customers/${this.tenancyId}/incidents`);

    try {
      const now = new Date();
      // Create a clean data object without undefined values
      const incidentData: Record<string, any> = {
        ...incident,
        summary: incident.summary,
        owner: incident.owner,
        status: incident.status,
        createdAt: now,
        updatedAt: now
      };

      // Only include auditId if it's defined
      if (incident.auditId) {
        incidentData.auditId = incident.auditId;
      } else {
        incidentData.auditId = null;
      }

      console.log('Creating incident with data:', incidentData);

      const ref = await addDoc(this.incidentsCollection(), incidentData);
      console.log('Incident created successfully with ID:', ref.id);

      // Return the full incident object with the generated ID
      return {
        id: ref.id,
        ...incidentData
      } as Incident;
    } catch (error) {
      console.error('Error in createIncident:', error);
      throw error; // Re-throw to be caught by the caller
    }
  }

  async updateIncident(incidentId: string, data: Partial<Incident>) {
    const ref = doc(this.db, `customers/${this.tenancyId}/incidents/${incidentId}`);
    const updateData: Record<string, any> = { ...data, updatedAt: new Date() };

    // Remove undefined values
    Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);

    await updateDoc(ref, updateData);
  }

  async deleteIncident(incidentId: string) {
    const ref = doc(this.db, `customers/${this.tenancyId}/incidents/${incidentId}`);
    await deleteDoc(ref);
  }

  // --- Link/Unlink Incident to Audit ---
  async linkIncidentToAudit(incidentId: string, auditId: string) {
    // Only need to update the incident with the auditId
    const incidentRef = doc(this.db, `customers/${this.tenancyId}/incidents/${incidentId}`);
    await updateDoc(incidentRef, {
      auditId,
      updatedAt: new Date()
    });
  }

  async unlinkIncidentFromAudit(incidentId: string) {
    // Just remove the auditId from the incident
    const incidentRef = doc(this.db, `customers/${this.tenancyId}/incidents/${incidentId}`);
    await updateDoc(incidentRef, {
      auditId: null,
      updatedAt: new Date()
    });
  }

  // --- Standalone Non-Conformities ---
  standaloneFindingsCollection() {
    return collection(this.db, `customers/${this.tenancyId}/findings`);
  }

  async listStandaloneFindings(): Promise<Finding[]> {
    const snap = await getDocs(this.standaloneFindingsCollection());
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Finding));
  }

  // --- Standalone Incidents ---
  standaloneIncidentsCollection() {
    return collection(this.db, `customers/${this.tenancyId}/incidents`);
  }

  async listStandaloneIncidents(): Promise<Incident[]> {
    const snap = await getDocs(this.standaloneIncidentsCollection());
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Incident));
  }

  async createStandaloneFinding(finding: Omit<Finding, 'id'> & { auditId?: string }) {
    const ref = this.standaloneFindingsCollection();
    if (!finding.auditId) {
      finding.auditId = null;
    }
    const docRef = await addDoc(ref, {
      ...finding,
      origin: 'standalone' as const,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  }

  async updateStandaloneFinding(findingId: string, data: Partial<Finding>) {
    const ref = doc(this.db, `customers/${this.tenancyId}/findings/${findingId}`);
    const updateData: Record<string, any> = { ...data, updatedAt: new Date() };

    // DED-973: Ensure linkedDocuments array is properly saved
    if ('linkedDocuments' in data) {
      updateData.linkedDocuments = Array.isArray(data.linkedDocuments) ? data.linkedDocuments : [];
    }

    // Remove undefined values
    Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);

    await updateDoc(ref, updateData);
  }

  async deleteStandaloneFinding(findingId: string) {
    const ref = doc(this.db, `customers/${this.tenancyId}/findings/${findingId}`);
    await deleteDoc(ref);
  }

  // --- Link/Unlink Standalone Finding to Audit ---
  async linkStandaloneFindingToAudit(findingId: string, auditId: string) {
    await this.updateStandaloneFinding(findingId, { auditId });
  }

  async unlinkStandaloneFindingFromAudit(findingId: string) {
    await this.updateStandaloneFinding(findingId, { auditId: null });
  }

  // Move a finding from an audit to the standalone findings collection
  async moveFindingToStandalone(auditId: string, findingId: string): Promise<string> {
    // Get the finding from the audit
    const findingRef = doc(this.db, `customers/${this.tenancyId}/audits/${auditId}/findings/${findingId}`);
    const findingSnap = await getDoc(findingRef);

    if (!findingSnap.exists()) {
      throw new Error(`Finding ${findingId} not found in audit ${auditId}`);
    }

    const findingData = findingSnap.data() as Omit<Finding, 'id'>;

    // Add to standalone findings collection
    const newFindingId = await this.createStandaloneFinding({
      ...findingData,
      origin: 'standalone' as const,
      auditId: null // Clear the auditId since we're unlinking
    });

    // Delete from audit's findings subcollection
    await deleteDoc(findingRef);

    return newFindingId;
  }
}
